/** @module qt.player
 *  @description 播放器
 */
import { v4 as uuidv4 } from 'uuid';
import { createAppCallback } from '../core';
import { invokeSchema } from '../base';

/** 播放内容枚举 */
export enum PlayContentTypeEnum {
  /** 电台
   * @version: 8.4.2+
   */
  RADIO = 0,
  /** 专辑
   * @version: 8.4.2+
   */
  CHANNEL = 1,
  /** 助眠 */
  SLEEP = 4,
  /** 听单
   * @version: 8.6.2+
   */
  TING = 6,
}

/**
 * 播放音频节目
 */
export function play({
  type = PlayContentTypeEnum.CHANNEL,
  typeId,
  programId,
  tingId,
}: {
  /** 播放类型 */
  type?: PlayContentTypeEnum;
  /** 播放类型ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  typeId: number;
  /** 节目 ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  programId: number;
  /** 听单 ID，当 type 为 ting 时必须 */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  tingId?: number;
}): Promise<unknown> {
  const params: any = {
    channel_id: typeId,
    program_id: programId,
    content_type: type,
  };
  if (type === PlayContentTypeEnum.TING) {
    params.combination_id = tingId;
  }
  return invokeSchema('qingtingfm://action.qingting.fm/player/play', params);
}

/**
 * 暂停播放
 * @example
 * ```javascript
 * qt.player.pause()
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function pause(): Promise<unknown> {
  return invokeSchema('qingtingfm://action.qingting.fm/player/pause');
}

/**
 * 获取播放状态
 * @example
 * ```
 * qt.player.getState()
  .then(function (res) {
    console.log(res.paused);
  })
  .catch(function (e) {
    console.log(e);
  });

 * ```
 */
export function getState(): Promise<{ paused: boolean }> {
  return invokeSchema('qingtingfm://action.qingting.fm/player/state') as Promise<{ paused: boolean }>;
}

/**
 * 获取当前播放的节目信息
 * @version: 8.1.7+
 */
export function getInfo(): Promise<{
  /** 专辑ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  channelId: string;
  /** 节目ID */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  programId: string;
  /** 是否暂停 */
  paused: boolean;
}> {
  return invokeSchema('qingtingfm://action.qingting.fm/playingnode', {}, true).then((res: any = {}) => {
    const data = res.data || {};
    return {
      channelId: data.channel_id,
      programId: data.program_id,
      paused: data.paused,
    };
  });
}

/**
 * 显示播放器
 * @example
 * ```
 * qt.player.show()
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });
 * ```
 */
export function show(): Promise<unknown> {
  return invokeSchema('qingtingfm://action.qingting.fm/showMiniPlayer', {
    show: true,
  });
}

/**
 * 隐藏播放器
 * @example
 * ```
 * qt.player.hide()
  .then(function (res) {
    console.log(res);
  })
  .catch(function (e) {
    console.log(e);
  });

 * ```
 */
export function hide(): Promise<unknown> {
  return invokeSchema('qingtingfm://action.qingting.fm/showMiniPlayer', {
    show: false,
  });
}

export interface Listener {
  clear?: () => void;
  cbName?: string;
}

/**
 * 添加播放监听，8.6.0
 * @example
 * ```
 * qt.player.stateListener({
  onChange: (res) => {
    console.log(res);
  }
})
 * ```
 */
export function stateListener({
  onChange,
}: {
  onChange: (res: {
    /** 返回状态 */
    code: number;
    /** 返回消息 */
    message: string;
    /** 返回数据 */
    data?: {
      /** 播放或暂停 (TODO: 应该只有两个可能的值,需要确定是哪两个) */
      state: '0' | '1' | '2';
      /** 播放内容的类型 */
      type: PlayContentTypeEnum;
      /** 专辑ID */
      channel_id?: string | number;
      /** 节目ID */
      program_id?: string | number;
    };
  }) => void;
}): Listener {
  const cb = createAppCallback(onChange, 'playStateListener', false);
  const listener: Listener = invokeSchema('qingtingfm://action.qingting.fm/addPlayStateListener', {
    play_state_change_listener: cb.cbName,
  }) as Listener;
  listener.clear = cb.clear;
  listener.cbName = cb.cbName;
  return listener;
}

/**
 * 客户端播放监听单例，同时可添加多个handler来接收播放监听回调的信息
 */

export class AppStateListener {
  /** 全局单例 */
  private static instance: AppStateListener;
  /** 内部维护一组监听器 */
  private handlers: Record<string, (jsonData: any) => void> = {};
  /** 内部响应播放状态,逐个调用监听器 */
  private listener;
  constructor() {
    // eslint-disable-next-line no-constructor-return
    if (AppStateListener.instance) return AppStateListener.instance;
    this.listener = stateListener({
      onChange: (res) => {
        const json = typeof res === 'string' ? JSON.parse(res) : res;
        // eslint-disable-next-line no-restricted-syntax
        for (const key of Object.keys(this.handlers)) {
          this.handlers[key](json.data || {});
        }
      },
    });
    AppStateListener.instance = this;
    // eslint-disable-next-line no-constructor-return
    return this;
  }

  /** 添加监听器 */
  public addHandler = (handler: (jsonData: any) => void): (() => void) => {
    const id = uuidv4();
    this.handlers[id] = handler;

    return () => {
      delete this.handlers[id];
    };
  };

  /** 清空监听器 */
  public clear = (): void => {
    this.handlers = {};
    if (this.listener && this.listener.clear) {
      this.listener.clear();
    }
  };
}

/** @ignore */
export const player = {
  play,
  pause,
  getInfo,
  getState,
  show,
  hide,
  stateListener,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  AppStateListener,
};
